import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import type { LucideIcon } from "lucide-react";
import { Swords, Dices, Bot } from "lucide-react";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useToolContext } from "../contexts/ToolContext";
import { useSessionContext } from "../contexts/SessionContext";
import { useLayoutContext } from "../contexts/LayoutContext";
import { useI18n } from "../i18n";
import { useToolbarDrag } from "../hooks/useToolbarDrag";
import { useToolOptions } from "../hooks/useToolOptions";
import { TOOL_GROUPS, TOOL_ICONS, TOOL_LABELS } from "../lib/tool-groups";
import type { ToolGroup } from "../lib/tool-groups";
import type {
  ToolName,
  FloatingToolbarProps,
  ToolOptionsFlyoutPassthrough,
} from "../types";
import UndoRedoButtons from "./UndoRedoButtons";
import ToolOptionsFlyout from "./ToolOptionsFlyout";
import { usePermissions } from "../hooks/usePermissions";

const TOOLBAR_WIDTH = 48;
const FLYOUT_WIDTH = 260;

/** Tools that render content in ToolOptionsFlyout */
const TOOLS_WITH_OPTIONS = new Set<ToolName>([
  "select",
  "erase",
  "draw",
  "shape",
  "fog",
  "measure",
  "location-link",
  "text",
  "note",
  "aoe",
  "wall",
  "inpaint-rect",
  "inpaint-lasso",
  "detail-rect",
]);

export default function FloatingToolbar({
  isGM: isGMProp,
  onInitiativeToggle,
  initiativeOpen,
  onDiceToggle,
  diceOpen,
  onAiToggle,
  aiOpen,
  toolOptionsProps,
  placement = 'floating',
}: FloatingToolbarProps) {
  const contained = placement === 'contained';
  const { state: toolState, setTool } = useToolContext();
  const sessionCtx = useSessionContext();
  const { state: layoutState, dispatch: layoutDispatch } = useLayoutContext();
  const { t } = useI18n();
  const { options: toolOptions } = useToolOptions();

  const isGM = isGMProp ?? sessionCtx.isGM;
  const permissions = usePermissions();
  const currentTool = toolState.active;
  const isHeld = toolState.previous !== null && !toolState.locked;

  const toolbarRef = useRef<HTMLDivElement>(null);

  const { position: dragPosition, isDragging, dragHandleProps } = useToolbarDrag({
    sidebarWidth: layoutState.sidebarWidth,
    sidebarOpen: layoutState.sidebarOpen,
    toolbarRef,
  });
  // Contained: the toolbar sits in the map pane; the tool-options flyout is
  // still window-fixed, so feed it the toolbar's measured viewport position
  // (re-measured on resize and on scroll of any ancestor pane).
  const [measured, setMeasured] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    if (!contained) return;
    const measure = (): void => {
      const rect = toolbarRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMeasured((prev) => (prev && prev.x === rect.left && prev.y === rect.top ? prev : { x: rect.left, y: rect.top }));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const container = toolbarRef.current?.parentElement;
    const ro = container ? new ResizeObserver(measure) : null;
    if (container && ro) ro.observe(container);
    // Two map panes side by side (group T): a pane can MOVE without changing its own size, and the options of the right
    // map stood next to the left one's toolbar. The panes of the workspace are watched for their size, and for their
    // class — a map beside the beat overview stands LEFT of it (order-first) and moves right, at the same width, when a
    // document takes the main pane.
    const workspace = toolbarRef.current?.closest('[data-testid="studio-workspace"]');
    const mo = workspace && typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
    if (workspace && ro) {
      ro.observe(workspace);
      workspace.querySelectorAll('[data-testid="focus-pane"], [data-testid="pinned-pane"]').forEach((pane) => {
        ro.observe(pane);
        mo?.observe(pane, { attributes: true, attributeFilter: ['class'] });
      });
    }
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [contained]);
  // Table (window-fixed toolbar): never above the map. The bars over it (the status strip, the breadcrumb of a nested map)
  // push the map down; a toolbar left at its old place sat on top of them and hid their words. The saved place is not touched.
  const [mapTop, setMapTop] = useState(0);
  useLayoutEffect(() => {
    if (contained) return;
    const measure = (): void => {
      const top = document.getElementById('canvas-container')?.getBoundingClientRect().top;
      if (top !== undefined) setMapTop((prev) => (prev === Math.round(top) ? prev : Math.round(top)));
    };
    measure();
    window.addEventListener('resize', measure);
    const host = document.getElementById('main-content');
    const ro = host ? new ResizeObserver(measure) : null;
    if (host && ro) ro.observe(host);
    const map = document.getElementById('canvas-container');
    if (map && ro) ro.observe(map);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [contained]);
  const position = contained && measured ? measured : { ...dragPosition, y: Math.max(dragPosition.y, mapTop > 0 ? mapTop + 8 : 0) };

  // Track last-used tool per group
  const [lastUsed, setLastUsed] = useState<Record<string, ToolName>>({});
  // Track which submenu strip is open
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  // Contained (GM Studio): only the button column scrolls; the open group's
  // strip is drawn beside it inside the non-scrolling toolbar, aligned with its
  // group button. A strip inside the scroll box would be clipped by it
  // (overflow-y:auto makes overflow-x auto too). Table: strips stay inline.
  const scrollRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef(new Map<string, HTMLDivElement>());
  const [stripTop, setStripTop] = useState<number | null>(null);
  const placeStrip = useCallback(() => {
    if (!contained || openGroupId === null) return;
    const group = groupRefs.current.get(openGroupId);
    const bar = toolbarRef.current;
    if (!group || !bar) return;
    const g = group.getBoundingClientRect();
    const b = bar.getBoundingClientRect();
    // Stays beside the toolbar even if its group is scrolled out of the column.
    setStripTop(Math.min(Math.max(g.top - b.top + g.height / 2, g.height / 2), b.height - g.height / 2));
  }, [contained, openGroupId]);
  useLayoutEffect(() => {
    placeStrip();
  }, [placeStrip]);

  // Close strip on outside click
  useEffect(() => {
    if (!openGroupId) return;
    const handler = (e: MouseEvent) => {
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(e.target as Node)
      ) {
        setOpenGroupId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openGroupId]);

  // Current page hasMap — used to conditionally show ai-edit tools
  const currentPageHasMap = useMemo(() => {
    const pageId = sessionCtx.outgoingPageId || sessionCtx.pages.activePageId;
    const pageInfo = sessionCtx.pages.pages.find((p) => p.id === pageId);
    return pageInfo?.hasMap ?? false;
  }, [
    sessionCtx.pages.pages,
    sessionCtx.pages.activePageId,
    sessionCtx.outgoingPageId,
  ]);

  // Filter groups by role and permissions; ai-edit requires GM + mapUrl
  const visibleGroups = useMemo(
    () =>
      TOOL_GROUPS.filter((g) => {
        if (g.id === "ai-edit" && !currentPageHasMap) return false;
        // V006: Drawing tools available to players when drawOnMap permission is granted
        if (g.id === "drawing") return isGM || permissions.drawOnMap;
        // V006: Note tool available to players when createNotes permission is granted
        if (g.id === "note") return isGM || permissions.createNotes;
        if (g.gmOnly && !isGM) return false;
        return true;
      }),
    [isGM, currentPageHasMap, permissions.drawOnMap, permissions.createNotes],
  );

  // Find which group the current tool belongs to
  const activeGroupId = useMemo(() => {
    for (const g of TOOL_GROUPS) {
      if (g.tools.includes(currentTool)) return g.id;
    }
    return null;
  }, [currentTool]);

  const handleToolSelect = useCallback(
    (tool: ToolName, groupId: string) => {
      setTool(tool);
      setLastUsed((prev) => ({ ...prev, [groupId]: tool }));
      setOpenGroupId(null);
      layoutDispatch({
        type: "layout:setToolOptions",
        visible: TOOLS_WITH_OPTIONS.has(tool),
      });
    },
    [setTool, layoutDispatch],
  );

  const getGroupIcon = (group: ToolGroup): LucideIcon => {
    const last = lastUsed[group.id];
    if (last && group.tools.includes(last)) {
      return TOOL_ICONS[last];
    }
    if (group.tools.includes(currentTool)) {
      return TOOL_ICONS[currentTool];
    }
    return group.defaultIcon;
  };

  const getGroupActiveTool = (group: ToolGroup): ToolName => {
    const last = lastUsed[group.id];
    if (last && group.tools.includes(last)) return last;
    if (group.tools.includes(currentTool)) return currentTool;
    return group.tools[0];
  };

  // Compute flyout side based on toolbar position
  const sidebarEffective = layoutState.sidebarOpen
    ? layoutState.sidebarWidth
    : 0;
  const flyoutSide: "left" | "right" =
    position.x + TOOLBAR_WIDTH + FLYOUT_WIDTH >
    window.innerWidth - (contained ? 0 : sidebarEffective)
      ? "left"
      : "right";

  // Does the current tool have options?
  const toolHasOptions = [
    "select",
    "draw",
    "erase",
    "shape",
    "fog",
    "measure",
    "location-link",
    "text",
    "note",
    "aoe",
    "map-object",
    "wall",
    "inpaint-rect",
    "inpaint-lasso",
    "detail-rect",
  ].includes(currentTool);

  /** State indicator dots for specific groups */
  const renderStateIndicator = (groupId: string) => {
    switch (groupId) {
      case "drawing":
        return (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-background"
            style={{ backgroundColor: toolOptions.drawColor }}
            data-testid="indicator-draw-color"
            title={toolOptions.drawColor}
          />
        );
      case "fog":
        if (toolOptions.reFogMode) {
          return (
            <span
              className="absolute -top-1 -right-1 text-label bg-destructive text-fg rounded-full px-0.5 leading-tight"
              data-testid="indicator-fog-refog"
            >
              re
            </span>
          );
        }
        return null;
      default:
        return null;
    }
  };

  /** Tool buttons of a group's slide-out strip (inline on the table, beside the column in Studio). */
  const renderStripTools = (group: ToolGroup) =>
    group.tools.map((tool) => {
      const Icon = TOOL_ICONS[tool];
      const label = TOOL_LABELS[tool];
      return (
        <Tooltip key={tool}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${currentTool === tool ? "bg-accent text-accent-foreground" : ""}`}
              onClick={() => handleToolSelect(tool, group.id)}
              // GK-10: an icon-only button needs its name for the keyboard and the screen reader («Стена» had none).
              aria-label={t(label)}
              data-testid={`toolbar-btn-${tool}`}
            >
              <Icon size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {tool === "ping" ? `${t(label)} — ${t("toolbar.pingHint")}` : t(label)}
          </TooltipContent>
        </Tooltip>
      );
    });

  /** Inline ToolSubmenu component (T005) — icon strip that slides out sideways */
  const renderToolSubmenu = (group: ToolGroup) => {
    const isActive = activeGroupId === group.id;
    const GroupIcon = getGroupIcon(group);
    const isOpen = openGroupId === group.id;

    return (
      <div
        key={group.id}
        ref={(el) => {
          if (el) groupRefs.current.set(group.id, el);
          else groupRefs.current.delete(group.id);
        }}
        className="relative"
        data-testid={`toolbar-group-${group.id}`}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`floating-toolbar-btn ${isActive ? "active" : ""} ${isActive && isHeld ? "tool-held" : ""}`}
              onClick={() => setOpenGroupId(isOpen ? null : group.id)}
              aria-label={`${t(group.labelKey)}${group.shortcutKey ? ` (${group.shortcutKey})` : ""}`}
              aria-pressed={isActive}
            >
              <GroupIcon size={18} />
              {renderStateIndicator(group.id)}
            </Button>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent side={flyoutSide}>
              {group.shortcutKey
                ? `${t(group.labelKey)} (${group.shortcutKey})`
                : t(group.labelKey)}
            </TooltipContent>
          )}
        </Tooltip>
        {/* Slide-out icon strip (table). In Studio it is drawn beside the scroll column. */}
        {!contained && (
          <div
            className="absolute flex items-center gap-0.5 rounded-lg border border-border bg-card/95 backdrop-blur-sm px-1 py-0.5 shadow-lg transition-all duration-150"
            style={{
              top: "50%",
              transform: `translateY(-50%) scale(${isOpen ? 1 : 0.9})`,
              [flyoutSide === "right" ? "left" : "right"]: "calc(100% + 10px)",
              opacity: isOpen ? 1 : 0,
              pointerEvents: isOpen ? "auto" : "none",
            }}
          >
            {renderStripTools(group)}
          </div>
        )}
      </div>
    );
  };

  const containedStripGroup = contained
    ? visibleGroups.find((g) => g.id === openGroupId && g.tools.length > 1 && !g.flyoutModes)
    : undefined;

  const renderGroup = (group: ToolGroup) => {
    const isActive = activeGroupId === group.id;
    const GroupIcon = getGroupIcon(group);
    const hasSub = group.tools.length > 1;

    if (hasSub && !group.flyoutModes) {
      return renderToolSubmenu(group);
    }

    // Single tool group or flyoutModes — direct click selects last-used tool, flyout handles sub-modes
    const tool = hasSub ? getGroupActiveTool(group) : group.tools[0];
    return (
      <Tooltip key={group.id}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`floating-toolbar-btn ${isActive ? "active" : ""} ${isActive && isHeld ? "tool-held" : ""}`}
            onClick={() => handleToolSelect(tool, group.id)}
            data-testid={`toolbar-group-${group.id}`}
            aria-label={`${t(group.labelKey)}${group.shortcutKey ? ` (${group.shortcutKey})` : ""}`}
            aria-pressed={isActive}
          >
            <div className="relative">
              <GroupIcon size={18} />
              {renderStateIndicator(group.id)}
            </div>
            {isActive && isHeld && (
              <span className="tool-held-badge text-label">
                {t("toolbar.held")}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side={flyoutSide}>
          {group.shortcutKey
            ? `${t(group.labelKey)} (${group.shortcutKey})`
            : t(group.labelKey)}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={toolbarRef}
        className="floating-toolbar"
        style={
          contained
            ? {
                position: "absolute",
                left: 8,
                top: 8,
                maxHeight: "calc(100% - 16px)",
                // Toolbar layer of the z-scale: above canvas overlays such as the
                // party portraits strip (--z-sidebar), below panels and dialogs.
                zIndex: "var(--z-toolbar)",
              }
            : {
                position: "fixed",
                left: position.x,
                top: position.y,
                zIndex: 'var(--z-overlay)',
                cursor: isDragging ? "grabbing" : undefined,
              }
        }
        data-testid="floating-toolbar"
        data-placement={placement}
        data-component="FloatingToolbar"
      >
        {/* Drag handle (the contained toolbar stays in its pane) */}
        {!contained && (
          <div
            className="floating-toolbar-drag-handle"
            role="separator"
            aria-label="Drag to reposition toolbar"
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
            {...dragHandleProps}
          >
            <div className="w-4 h-0.5 rounded-full bg-muted-foreground/40" />
            <div className="w-4 h-0.5 rounded-full bg-muted-foreground/40 mt-0.5" />
          </div>
        )}

        {/* Contained: only this column scrolls. Table: display:contents — the
            sections stay direct flex items of the toolbar, layout unchanged. */}
        <div
          ref={scrollRef}
          className={contained ? "floating-toolbar-scroll" : "contents"}
          style={
            contained
              ? { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minHeight: 0, overflowY: "auto" }
              : undefined
          }
          onScroll={contained ? placeStrip : undefined}
        >
        {/* Undo/Redo */}
        <div className="floating-toolbar-section">
          <UndoRedoButtons />
        </div>

        {/* Separator */}
        <div className="w-6 h-px bg-border mx-auto" />

        {/* Tool groups */}
        <div className="floating-toolbar-section">
          {visibleGroups.map(renderGroup)}
        </div>

        {/* Action toggles — initiative + dice (all participants). The
            initiative button was GM-only, which left players unable to
            reopen a lost panel (owner feedback 2026-07-08); a player's
            toggle is local-only (the GM toggle still drives everyone —
            toggleInitiativePanel gates the broadcast on isGM). */}
        {(onInitiativeToggle || onDiceToggle || onAiToggle) && <div className="w-6 h-px bg-border mx-auto" />}
        <div className="floating-toolbar-section">
          {onInitiativeToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`floating-toolbar-btn ${initiativeOpen ? "active" : ""}`}
                onClick={onInitiativeToggle}
                aria-label={`${t("toolbar.initiative")} (I)`}
                aria-pressed={initiativeOpen}
                data-testid="toolbar-initiative-toggle"
              >
                <Swords size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side={flyoutSide}>
              {t("toolbar.initiative")} (I)
            </TooltipContent>
          </Tooltip>
          )}
          {onDiceToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`floating-toolbar-btn ${diceOpen ? "active" : ""}`}
                onClick={onDiceToggle}
                aria-label={t("dice.title")}
                aria-pressed={diceOpen}
                data-testid="toolbar-dice-toggle"
              >
                <Dices size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side={flyoutSide}>{t("dice.title")}</TooltipContent>
          </Tooltip>
          )}
          {onAiToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`floating-toolbar-btn ${aiOpen ? "active" : ""}`}
                onClick={onAiToggle}
                aria-label={t("ai.title")}
                aria-pressed={aiOpen}
                data-testid="toolbar-ai-toggle"
              >
                <Bot size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side={flyoutSide}>{t("ai.title")}</TooltipContent>
          </Tooltip>
          )}
        </div>
        </div>

        {/* Contained: the open group's strip, outside the scroll box, next to its button. */}
        {containedStripGroup && stripTop !== null && (
          <div
            className="absolute flex items-center gap-0.5 rounded-lg border border-border bg-card/95 backdrop-blur-sm px-1 py-0.5 shadow-lg"
            data-testid={`toolbar-strip-${containedStripGroup.id}`}
            style={{
              top: stripTop,
              transform: "translateY(-50%)",
              [flyoutSide === "right" ? "left" : "right"]: "calc(100% + 10px)",
            }}
          >
            {renderStripTools(containedStripGroup)}
          </div>
        )}
      </div>

      {/* Tool options flyout — T012/T013. In Studio it steps aside while a strip
          is open: both sit right of the toolbar, and its layer would cover the strip. */}
      {toolHasOptions && !containedStripGroup && (
        <ToolOptionsFlyout
          {...toolOptionsProps}
          isGM={isGM}
          side={flyoutSide}
          toolbarPosition={position}
          toolbarWidth={TOOLBAR_WIDTH}
        />
      )}
    </TooltipProvider>
  );
}
